import { useCallback, useEffect, useState } from "react"

/*
 * Consimtamant pentru cookie-uri, aliniat cu jobbin.ro/cookies.html.
 *
 * Reguli respectate:
 *  - nimic in afara categoriei strict necesare nu se activeaza fara acord explicit;
 *  - utilizarea platformei NU echivaleaza cu acordul (art. 4 pct. 11 GDPR, cons. 32);
 *  - refuzul cere acelasi numar de clicuri ca acceptarea;
 *  - acordul se retrage la fel de usor cum se acorda (art. 7 alin. 3 GDPR), prin
 *    openCookiePreferences();
 *  - alegerea expira cand se schimba versiunea politicii.
 */

const CONSENT_KEY = "jobbin_cookie_consent"
const CONSENT_VERSION = 1

export type ConsentCategory = "functionale" | "analiza" | "marketing"

export type ConsentRecord = {
    version: number
    date: string | null
    functionale: boolean
    analiza: boolean
    marketing: boolean
}

const emptyConsent = (): ConsentRecord => ({
    version: CONSENT_VERSION,
    date: null,
    functionale: false,
    analiza: false,
    marketing: false,
})

export const readConsent = (): ConsentRecord | null => {
    try {
        const raw = localStorage.getItem(CONSENT_KEY)
        if (!raw) return null

        const parsed = JSON.parse(raw) as Partial<ConsentRecord>
        // o versiune veche a politicii inseamna ca acordul trebuie cerut din nou
        if (parsed.version !== CONSENT_VERSION) return null

        return { ...emptyConsent(), ...parsed }
    } catch {
        return null
    }
}

export const hasConsent = (category: ConsentCategory): boolean =>
    Boolean(readConsent()?.[category])

/** Redeschide centrul de preferinte din orice punct al aplicatiei. */
export const openCookiePreferences = () => {
    window.dispatchEvent(new CustomEvent("jobbin:open-cookie-preferences"))
}

type Choices = Record<ConsentCategory, boolean>

const CATEGORIES: { key: ConsentCategory; title: string; description: string }[] = [
    {
        key: "functionale",
        title: "Funcționale",
        description:
            "Rețin preferințe care îmbunătățesc experiența, cum ar fi ultimele filtre folosite. Fără ele platforma funcționează, dar uită aceste setări.",
    },
    {
        key: "analiza",
        title: "Analiză",
        description:
            "Ne-ar ajuta să înțelegem cum este folosită platforma. În acest moment nu folosim niciun instrument de analiză.",
    },
    {
        key: "marketing",
        title: "Marketing",
        description:
            "Ar permite măsurarea campaniilor și afișarea de reclame relevante. În acest moment nu folosim cookie-uri de marketing.",
    },
]

export const CookieBanner = () => {
    const [visible, setVisible] = useState(false)
    const [showPreferences, setShowPreferences] = useState(false)
    const [choices, setChoices] = useState<Choices>({
        functionale: false,
        analiza: false,
        marketing: false,
    })

    useEffect(() => {
        const stored = readConsent()

        if (stored) {
            setChoices({
                functionale: stored.functionale,
                analiza: stored.analiza,
                marketing: stored.marketing,
            })
        } else {
            setVisible(true)
        }

        const reopen = () => {
            const current = readConsent()
            if (current) {
                setChoices({
                    functionale: current.functionale,
                    analiza: current.analiza,
                    marketing: current.marketing,
                })
            }
            setShowPreferences(true)
        }

        window.addEventListener("jobbin:open-cookie-preferences", reopen)
        return () => window.removeEventListener("jobbin:open-cookie-preferences", reopen)
    }, [])

    const save = useCallback((selection: Choices) => {
        const record: ConsentRecord = {
            ...selection,
            version: CONSENT_VERSION,
            date: new Date().toISOString(),
        }

        try {
            localStorage.setItem(CONSENT_KEY, JSON.stringify(record))
        } catch {
            // navigare privata sau stocare blocata: continuam fara a persista alegerea
        }

        setChoices(selection)
        setShowPreferences(false)
        setVisible(false)

        // consumatorii de consimtamant reactioneaza la schimbare fara reincarcarea paginii
        window.dispatchEvent(new CustomEvent("jobbin:consent-changed", { detail: record }))
    }, [])

    const acceptAll = () => save({ functionale: true, analiza: true, marketing: true })
    const rejectAll = () => save({ functionale: false, analiza: false, marketing: false })

    if (!visible && !showPreferences) return null

    if (showPreferences) {
        return (
            <div
                className="fixed inset-0 z-[60] flex items-center justify-center bg-black/40 p-4"
                role="dialog"
                aria-modal="true"
                aria-labelledby="cookie-prefs-title"
                onClick={(e) => {
                    if (e.target === e.currentTarget) setShowPreferences(false)
                }}
            >
                <div className="max-h-[85vh] w-full max-w-xl overflow-y-auto rounded-2xl bg-white p-6 shadow-2xl sm:p-8">
                    <h2 id="cookie-prefs-title" className="text-xl font-bold text-figmaBlack">
                        Preferințe cookie-uri
                    </h2>
                    <p className="mt-2 text-sm leading-relaxed text-figmaDarkGray">
                        Alege ce categorii accepți. Îți poți schimba opțiunea oricând din meniul contului.
                    </p>

                    <div className="mt-5 rounded-xl border border-gray-200 p-4">
                        <div className="flex items-center justify-between gap-4">
                            <h3 className="text-sm font-semibold text-figmaBlack">Strict necesare</h3>
                            <span className="shrink-0 text-xs text-figmaDarkGray">Mereu active</span>
                        </div>
                        <p className="mt-1 text-xs leading-relaxed text-figmaDarkGray">
                            Mențin sesiunea ta de autentificare și rețin chiar alegerea privind cookie-urile.
                            Fără ele platforma nu poate funcționa, deci nu pot fi dezactivate.
                        </p>
                    </div>

                    {CATEGORIES.map((category) => (
                        <div key={category.key} className="mt-3 rounded-xl border border-gray-200 p-4">
                            <div className="flex items-center justify-between gap-4">
                                <h3 className="text-sm font-semibold text-figmaBlack">{category.title}</h3>
                                <label className="relative inline-flex h-6 w-11 shrink-0 cursor-pointer items-center">
                                    <input
                                        type="checkbox"
                                        className="peer sr-only"
                                        checked={choices[category.key]}
                                        onChange={(e) =>
                                            setChoices({ ...choices, [category.key]: e.target.checked })
                                        }
                                        aria-label={`Cookie-uri ${category.title.toLowerCase()}`}
                                    />
                                    <span className="h-6 w-11 rounded-full bg-gray-300 transition-colors peer-checked:bg-figmaPrimary" />
                                    <span className="absolute left-1 h-4 w-4 rounded-full bg-white transition-transform peer-checked:translate-x-5" />
                                </label>
                            </div>
                            <p className="mt-1 text-xs leading-relaxed text-figmaDarkGray">
                                {category.description}
                            </p>
                        </div>
                    ))}

                    <div className="mt-6 flex flex-wrap justify-end gap-3">
                        <button
                            type="button"
                            onClick={() => setShowPreferences(false)}
                            className="px-4 py-2 text-sm font-semibold text-figmaDarkGray underline"
                        >
                            Închide
                        </button>
                        <button
                            type="button"
                            onClick={rejectAll}
                            className="rounded-xl border border-gray-300 px-5 py-2 text-sm font-semibold text-figmaDarkGray transition-colors hover:bg-gray-50"
                        >
                            Refuz toate
                        </button>
                        <button
                            type="button"
                            onClick={() => save(choices)}
                            className="rounded-xl border border-gray-300 px-5 py-2 text-sm font-semibold text-figmaBlack transition-colors hover:bg-gray-50"
                        >
                            Salvează alegerea
                        </button>
                        <button
                            type="button"
                            onClick={acceptAll}
                            className="rounded-xl bg-figmaPrimary px-5 py-2 text-sm font-semibold text-white transition-opacity hover:opacity-90"
                        >
                            Accept toate
                        </button>
                    </div>
                </div>
            </div>
        )
    }

    return (
        <div
            className="fixed bottom-0 left-0 right-0 z-50 border-t border-gray-200 bg-white px-4 py-4 shadow-xl"
            role="dialog"
            aria-labelledby="cookie-banner-title"
        >
            <div className="mx-auto flex max-w-[1600px] flex-col items-start gap-3 sm:flex-row sm:items-center sm:gap-8">
                <div className="flex-1">
                    <h2 id="cookie-banner-title" className="text-sm font-bold text-figmaBlack">
                        Folosim cookie-uri
                    </h2>
                    <p className="mt-1 text-sm leading-relaxed text-figmaDarkGray">
                        Cookie-urile strict necesare mențin sesiunea ta de autentificare. Pentru celelalte
                        categorii avem nevoie de acordul tău. Detalii în{" "}
                        <a
                            href="https://jobbin.ro/cookies.html"
                            target="_blank"
                            rel="noreferrer"
                            className="font-semibold text-figmaPrimary underline"
                        >
                            Politica de cookie-uri
                        </a>
                        .
                    </p>
                </div>
                <div className="flex shrink-0 flex-wrap gap-3">
                    <button
                        type="button"
                        onClick={() => setShowPreferences(true)}
                        className="px-3 py-2 text-sm font-semibold text-figmaDarkGray underline"
                    >
                        Setări
                    </button>
                    <button
                        type="button"
                        onClick={rejectAll}
                        className="rounded-xl border border-gray-300 px-5 py-2 text-sm font-semibold text-figmaDarkGray transition-colors hover:bg-gray-50"
                    >
                        Refuz
                    </button>
                    <button
                        type="button"
                        onClick={acceptAll}
                        className="rounded-xl bg-figmaPrimary px-5 py-2 text-sm font-semibold text-white transition-opacity hover:opacity-90"
                    >
                        Accept toate
                    </button>
                </div>
            </div>
        </div>
    )
}
